import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Breadcrumb as BreadcrumbRoot,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { useBreadcrumbs, type Crumb } from '@/shared/hooks/use-breadcrumbs';

const MAX_VISIBLE = 3;
const INTERMEDIATE_CAP = 28;
const TERMINAL_CAP = 40;

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1) + '…';
}

export function Breadcrumb() {
  const { t } = useTranslation('common');
  const crumbs = useBreadcrumbs();

  const head = crumbs[0];
  if (!head) return null;

  const collapsed = crumbs.length > MAX_VISIBLE;
  const tail = crumbs.length > 1 ? crumbs[crumbs.length - 1] : null;
  const middle: Crumb[] = crumbs.slice(1, -1);
  const visibleMiddle: Crumb[] = collapsed ? [] : middle;
  const collapsedMiddle: Crumb[] = collapsed ? middle : [];

  return (
    <BreadcrumbRoot aria-label={t('breadcrumb.label')}>
      <BreadcrumbList>
        <BreadcrumbItem>{renderCrumb(head, INTERMEDIATE_CAP, head.isCurrent)}</BreadcrumbItem>

        {visibleMiddle.map((crumb) => (
          <FragmentWithSeparator key={crumb.href ?? crumb.label}>
            <BreadcrumbItem>{renderCrumb(crumb, INTERMEDIATE_CAP, false)}</BreadcrumbItem>
          </FragmentWithSeparator>
        ))}

        {collapsed && (
          <FragmentWithSeparator>
            <BreadcrumbItem>
              <DropdownMenu>
                <DropdownMenuTrigger
                  aria-label={t('breadcrumb.collapsed')}
                  className="hover:text-text transition-colors"
                >
                  <BreadcrumbEllipsis />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {collapsedMiddle.map((crumb) => (
                    <DropdownMenuItem key={crumb.href ?? crumb.label} asChild>
                      {crumb.href ? (
                        <Link to={crumb.href}>{truncate(crumb.fullLabel, INTERMEDIATE_CAP)}</Link>
                      ) : (
                        <span>{truncate(crumb.fullLabel, INTERMEDIATE_CAP)}</span>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </BreadcrumbItem>
          </FragmentWithSeparator>
        )}

        {tail && (
          <FragmentWithSeparator>
            <BreadcrumbItem>{renderCrumb(tail, TERMINAL_CAP, true)}</BreadcrumbItem>
          </FragmentWithSeparator>
        )}
      </BreadcrumbList>
    </BreadcrumbRoot>
  );
}

function renderCrumb(crumb: Crumb, cap: number, isCurrent: boolean) {
  const display = truncate(crumb.fullLabel, cap);
  const truncated = display !== crumb.fullLabel;

  if (isCurrent || !crumb.href) {
    return (
      <BreadcrumbPage title={truncated ? crumb.fullLabel : undefined}>{display}</BreadcrumbPage>
    );
  }
  return (
    <BreadcrumbLink asChild>
      <Link to={crumb.href} title={truncated ? crumb.fullLabel : undefined}>
        {display}
      </Link>
    </BreadcrumbLink>
  );
}

function FragmentWithSeparator({ children }: { children: React.ReactNode }) {
  return (
    <>
      <BreadcrumbSeparator>
        <span aria-hidden="true">/</span>
      </BreadcrumbSeparator>
      {children}
    </>
  );
}
